// Dummy data — UI prototype, nothing is wired up. Structure lifted from the Operating
// Effectiveness control-testing module (test procedures → sample → item testing → conclusion).
import {PROCEDURES} from "../../../OeDemo/procedures";
import {FieldType} from "./populationsData";

export type TestStatus = "Not started" | "In progress" | "Complete";

/** 1–5 effectiveness scale, mirroring OE's numeric scoring. */
export const EFFECTIVENESS_LEVELS: {score: number; label: string; color: string}[] = [
  {score: 1, label: "Ineffective", color: "red"},
  {score: 2, label: "Significant deficiencies", color: "orange"},
  {score: 3, label: "Deficiencies noted", color: "yellow"},
  {score: 4, label: "Effective", color: "green"},
  {score: 5, label: "Highly effective", color: "teal"},
];

export const effectivenessLevel = (score: number) => EFFECTIVENESS_LEVELS.find(l => l.score === score);

export type ItemOutcome = "Pass" | "Fail" | "Unknown";

export const OUTCOME_COLOR: Record<ItemOutcome, string> = {Pass: "green", Fail: "red", Unknown: "gray"};

/** A field the agent extracts from each item's evidence. */
export type ExtractionField = {key: string; label: string; type: FieldType; guidance?: string};

/** How a criterion's pass/fail verdict is reached. */
export type CriterionEvaluation = "ai" | "rule";

/** A pass/fail test applied to each item — judged by AI, or evaluated by a deterministic rule. */
export type TestCriterion = {
  key: string;
  name: string;
  /** Defaults to "ai" when omitted. "rule" → verdict comes from `expression`, not AI judgment. */
  evaluation?: CriterionEvaluation;
  /** AI criteria: the natural-language hint the agent judges against. */
  guidance?: string;
  /** Rule criteria: the DSL expression, over extracted and/or source/population field keys. */
  expression?: string;
};

/** A criterion's evaluation type, treating the absence of `evaluation` as AI (back-compat). */
export const criterionEval = (c: TestCriterion): CriterionEvaluation => c.evaluation ?? "ai";

export type ItemResult = {outcome: ItemOutcome; justification?: string};

/**
 * Per-item testing data. `items[i]` aligns with the linked sample's population row `i` — the row
 * supplies the population fields, this supplies the extracted data + per-criterion results.
 */
export type TestItem = {
  extracted: Record<string, string>;
  results: Record<string, ItemResult>;
  evidenceCount: number;
  /** The evidence files attached to this item — shown alongside the extracted data for review. */
  evidence?: string[];
};

/**
 * A reusable procedure from the library — the starting point for a test. A procedure is the *test
 * plan*: the pass/fail criteria we're trying to satisfy. (Evidence-extraction fields are not part of
 * the template — they're inferred per-test from the evidence + criteria; see `ControlTest`.)
 */
export type TestProcedure = {id: string; name: string; criteria: TestCriterion[]};

/** Stable key for a criterion or field, so the plan and the test agree on one. */
export const slugKey = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * The procedure library, as the testing surfaces see it — the same one the plan draws on. A test
 * that started from an entry has to be diffable against that entry, which it can't be if the two
 * sides keep their own copies of it.
 */
export const TEST_PROCEDURES: TestProcedure[] = PROCEDURES.map(procedure => ({
  id: procedure.id,
  name: procedure.name,
  criteria: procedure.criteria.map(c => ({key: slugKey(c.name), name: c.name, guidance: c.guidance})),
}));

/** A control this test provides assurance over (lives in the audit plan). */
export type ControlRef = {ref: string; name: string};

export type ControlTest = {
  id: string;
  name: string;
  description: string;
  /** The control being tested, e.g. "JE-01 — Manual journal authorisation" (display in the list). */
  control: string;
  /** The control(s) this test is linked to. */
  controls: ControlRef[];
  /** The library procedure this test started from. */
  baseProcedureId: string;
  /** The sample this test draws on. */
  sampleId: string;
  sampleName: string;
  status: TestStatus;
  /** Concluded effectiveness (1–5), absent until the test is concluded. */
  effectivenessScore?: number;
  effectivenessJustification?: string;
  extractionFields: ExtractionField[];
  criteria: TestCriterion[];
  items: TestItem[];
  /** Uploaded evidence not yet matched to a specific item — shown in a tray beneath the table. */
  unmatchedEvidence?: string[];
};
